import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  Upload, Camera, Trash2, Eye, Image, Video, X,
  Grid3X3, List, Ban, AlertCircle, CheckCircle, Clock,
  ChevronLeft, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const isVideo = (photo) => {
  return photo.file_type === 'video' || (photo.mime_type && photo.mime_type.startsWith('video/'));
};

export default function EventPhotosGrid({ eventId }) {
  const { getAuthHeader } = useAuth();
  const fileInputRef = useRef(null);

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [isDragging, setIsDragging] = useState(false);
  const [uploads, setUploads] = useState([]); // { file, progress, status }
  const [lightbox, setLightbox] = useState(null); // photo object or null

  const loadPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/photos`, { headers: getAuthHeader() });
      setPhotos(res.data.photos || []);
    } catch (err) {
      console.error('Failed to load photos:', err);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [eventId, getAuthHeader]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);

    const initialUploads = fileList.map((f) => ({
      file: f,
      progress: 0,
      status: 'uploading'
    }));
    setUploads(initialUploads);

    try {
      const urlRes = await axios.post(
        `${API}/events/${eventId}/photos/bulk-upload-urls`,
        { files: fileList.map((f) => ({ file_name: f.name, mime_type: f.type })) },
        { headers: getAuthHeader() }
      );

      const uploadData = urlRes.data;

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const { upload_url, s3_key } = uploadData[i];

        try {
          await axios.put(upload_url, file, {
            headers: { 'Content-Type': file.type },
            onUploadProgress: (p) => {
              const pct = p.total ? p.loaded / p.total : 0;
              setUploads((prev) =>
                prev.map((u, idx) => (idx === i ? { ...u, progress: pct } : u))
              );
            }
          });

          setUploads((prev) =>
            prev.map((u, idx) => (idx === i ? { ...u, progress: 1, status: 'registering' } : u))
          );

          await axios.post(
            `${API}/events/${eventId}/photos`,
            { s3_key, original_filename: file.name, mime_type: file.type, file_size: file.size },
            { headers: getAuthHeader() }
          );

          setUploads((prev) =>
            prev.map((u, idx) => (idx === i ? { ...u, status: 'done' } : u))
          );
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
          setUploads((prev) =>
            prev.map((u, idx) => (idx === i ? { ...u, status: 'error' } : u))
          );
        }
      }

      toast.success(`Uploaded ${fileList.length} file${fileList.length > 1 ? 's' : ''}`);
      await loadPhotos();
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Failed to start upload');
      setUploads((prev) => prev.map((u) => ({ ...u, status: 'error' })));
    }

    setTimeout(() => setUploads([]), 3000);
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}/photos/${photoId}`, { headers: getAuthHeader() });
      setPhotos((prev) => prev.filter((p) => p.photo_id !== photoId));
      if (lightbox?.photo_id === photoId) setLightbox(null);
      toast.success('Photo deleted');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete photo');
    }
  };

  const toggleDoNotUse = async (photo) => {
    const newVal = !photo.do_not_use_marketing;
    try {
      await axios.put(
        `${API}/events/${eventId}/photos/${photo.photo_id}`,
        { do_not_use_marketing: newVal },
        { headers: getAuthHeader() }
      );
      setPhotos((prev) =>
        prev.map((p) =>
          p.photo_id === photo.photo_id ? { ...p, do_not_use_marketing: newVal } : p
        )
      );
      if (lightbox?.photo_id === photo.photo_id) {
        setLightbox((prev) => ({ ...prev, do_not_use_marketing: newVal }));
      }
      toast.success(newVal ? 'Marked as DO NOT USE' : 'Cleared DO NOT USE flag');
    } catch (err) {
      console.error('Toggle failed:', err);
      toast.error('Failed to update photo');
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleUpload(e.dataTransfer.files);
  };

  // Lightbox navigation
  const navigateLightbox = (dir) => {
    if (!lightbox) return;
    const idx = photos.findIndex((p) => p.photo_id === lightbox.photo_id);
    const next = idx + dir;
    if (next >= 0 && next < photos.length) {
      setLightbox(photos[next]);
    }
  };

  const photoCount = photos.filter((p) => !isVideo(p)).length;
  const videoCount = photos.filter((p) => isVideo(p)).length;

  const FaceStatusDot = ({ status }) => {
    if (status === 'completed') return <span className="w-2 h-2 rounded-full bg-green-500 inline-block" title="Face detection completed" />;
    if (status === 'pending' || status === 'processing') return <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" title="Face detection pending" />;
    if (status === 'failed') return <span className="w-2 h-2 rounded-full bg-red-500 inline-block" title="Face detection failed" />;
    return null;
  };

  // --- Render ---

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-stone-400">
        <div className="animate-spin w-6 h-6 border-2 border-stone-300 border-t-stone-600 rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {photoCount} Photo{photoCount !== 1 ? 's' : ''}
          {videoCount > 0 && `, ${videoCount} Video${videoCount !== 1 ? 's' : ''}`}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg border transition ${viewMode === 'grid' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200 hover:bg-stone-50'}`}
            title="Grid view"
          >
            <Grid3X3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg border transition ${viewMode === 'list' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200 hover:bg-stone-50'}`}
            title="List view"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white rounded-lg hover:bg-stone-800 transition text-sm font-medium"
          >
            <Upload className="w-4 h-4" />
            Upload
          </button>
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${
          isDragging
            ? 'border-stone-500 bg-stone-100'
            : 'border-stone-200 bg-stone-50 hover:border-stone-300 hover:bg-stone-100'
        }`}
      >
        <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
        <p className="text-sm text-stone-600">Drag & drop photos and videos here, or click to browse</p>
        <p className="text-xs text-stone-400 mt-1">Supports images and videos</p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => {
          handleUpload(e.target.files);
          e.target.value = '';
        }}
      />

      {/* Upload progress */}
      {uploads.length > 0 && (
        <div className="space-y-2 bg-white border border-stone-200 rounded-lg p-4">
          <p className="text-sm font-medium text-stone-700">Uploading...</p>
          {uploads.map((u, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-xs text-stone-600 truncate w-40">{u.file.name}</span>
              <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    u.status === 'error' ? 'bg-red-500' : u.status === 'done' ? 'bg-green-500' : 'bg-stone-700'
                  }`}
                  style={{ width: `${Math.round(u.progress * 100)}%` }}
                />
              </div>
              <span className="text-xs text-stone-500 w-20 text-right">
                {u.status === 'uploading' && `${Math.round(u.progress * 100)}%`}
                {u.status === 'registering' && 'Saving...'}
                {u.status === 'done' && <span className="text-green-600">Done</span>}
                {u.status === 'error' && <span className="text-red-600">Failed</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {photos.length === 0 && uploads.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-stone-400">
          <Camera className="w-12 h-12 mb-3" />
          <p className="text-lg font-medium text-stone-500" style={{ fontFamily: 'Manrope, sans-serif' }}>No photos yet</p>
          <p className="text-sm mt-1">Upload event photos to get started</p>
        </div>
      )}

      {/* Grid view */}
      {photos.length > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {photos.map((photo) => (
            <div
              key={photo.photo_id}
              className="group relative bg-white border border-stone-200 rounded-lg overflow-hidden cursor-pointer"
              onClick={() => setLightbox(photo)}
            >
              <div className="aspect-square relative">
                {isVideo(photo) ? (
                  <div className="w-full h-full bg-stone-100 flex items-center justify-center">
                    <Video className="w-10 h-10 text-stone-400" />
                  </div>
                ) : (
                  <img
                    src={photo.url}
                    alt={photo.display_filename || photo.original_filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}

                {/* Video play overlay */}
                {isVideo(photo) && photo.url && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
                      <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[14px] border-l-white border-b-[8px] border-b-transparent ml-1" />
                    </div>
                  </div>
                )}

                {/* DO NOT USE badge */}
                {photo.do_not_use_marketing && (
                  <div className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                    DO NOT USE
                  </div>
                )}

                {/* Face detection + count badges */}
                <div className="absolute top-2 right-2 flex items-center gap-1">
                  <FaceStatusDot status={photo.face_detection_status} />
                  {photo.detected_faces_count > 0 && (
                    <span className="bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                      {photo.detected_faces_count} face{photo.detected_faces_count !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Bottom gradient overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 pt-6">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-xs truncate max-w-[70%]">
                      {photo.display_filename || photo.original_filename}
                    </span>
                    {photo.photo_number && (
                      <span className="bg-white/20 text-white text-[10px] px-1.5 py-0.5 rounded">
                        #{photo.photo_number}
                      </span>
                    )}
                  </div>
                </div>

                {/* Hover actions */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleDoNotUse(photo); }}
                    className={`p-2 rounded-full transition ${
                      photo.do_not_use_marketing
                        ? 'bg-white text-stone-700 hover:bg-stone-100'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                    title={photo.do_not_use_marketing ? 'Clear DO NOT USE' : 'Mark DO NOT USE'}
                  >
                    <Ban className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(photo.photo_id); }}
                    className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List view */}
      {photos.length > 0 && viewMode === 'list' && (
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50 text-stone-500 text-xs uppercase tracking-wider">
                <th className="text-left py-2 px-3">File</th>
                <th className="text-left py-2 px-3 hidden sm:table-cell">Type</th>
                <th className="text-left py-2 px-3 hidden md:table-cell">Size</th>
                <th className="text-center py-2 px-3">Do Not Use</th>
                <th className="text-center py-2 px-3 hidden lg:table-cell">Faces</th>
                <th className="text-left py-2 px-3 hidden lg:table-cell">Uploaded By</th>
                <th className="text-left py-2 px-3 hidden md:table-cell">Date</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {photos.map((photo) => (
                <tr
                  key={photo.photo_id}
                  className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition"
                  onClick={() => setLightbox(photo)}
                >
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-stone-100">
                        {isVideo(photo) ? (
                          <div className="w-full h-full flex items-center justify-center">
                            <Video className="w-5 h-5 text-stone-400" />
                          </div>
                        ) : (
                          <img src={photo.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                        )}
                      </div>
                      <span className="text-stone-700 truncate max-w-[200px]">
                        {photo.display_filename || photo.original_filename}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 px-3 hidden sm:table-cell">
                    <span className="text-stone-500 capitalize">{photo.file_type || (isVideo(photo) ? 'video' : 'image')}</span>
                  </td>
                  <td className="py-2 px-3 hidden md:table-cell text-stone-500">{formatSize(photo.file_size)}</td>
                  <td className="py-2 px-3 text-center">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleDoNotUse(photo); }}
                      className={`inline-flex items-center justify-center w-8 h-5 rounded-full transition ${
                        photo.do_not_use_marketing
                          ? 'bg-red-500'
                          : 'bg-stone-200'
                      }`}
                      title={photo.do_not_use_marketing ? 'Clear DO NOT USE' : 'Mark DO NOT USE'}
                    >
                      <span
                        className={`block w-3.5 h-3.5 rounded-full bg-white transition transform ${
                          photo.do_not_use_marketing ? 'translate-x-1.5' : '-translate-x-1.5'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="py-2 px-3 text-center hidden lg:table-cell">
                    <div className="flex items-center justify-center gap-1.5">
                      <FaceStatusDot status={photo.face_detection_status} />
                      {photo.detected_faces_count > 0 && (
                        <span className="text-stone-500 text-xs">{photo.detected_faces_count}</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 hidden lg:table-cell text-stone-500">{photo.uploaded_by_name || '-'}</td>
                  <td className="py-2 px-3 hidden md:table-cell text-stone-500 text-xs">{formatDate(photo.created_at)}</td>
                  <td className="py-2 px-3 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(photo.photo_id); }}
                      className="p-1.5 text-stone-400 hover:text-red-600 rounded transition"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative bg-white rounded-xl overflow-hidden max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col md:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-3 right-3 z-10 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Image area */}
            <div className="flex-1 bg-stone-900 flex items-center justify-center relative min-h-[300px]">
              {isVideo(lightbox) ? (
                lightbox.url ? (
                  <video src={lightbox.url} controls className="max-w-full max-h-[70vh]" />
                ) : (
                  <Video className="w-16 h-16 text-stone-500" />
                )
              ) : (
                <img
                  src={lightbox.url}
                  alt={lightbox.display_filename || lightbox.original_filename}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              )}

              {/* Nav arrows */}
              {photos.findIndex((p) => p.photo_id === lightbox.photo_id) > 0 && (
                <button
                  onClick={() => navigateLightbox(-1)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              {photos.findIndex((p) => p.photo_id === lightbox.photo_id) < photos.length - 1 && (
                <button
                  onClick={() => navigateLightbox(1)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Details sidebar */}
            <div className="w-full md:w-72 border-t md:border-t-0 md:border-l border-stone-200 p-5 space-y-4 overflow-y-auto max-h-[40vh] md:max-h-[90vh]">
              <h4 className="font-semibold text-stone-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Photo Details
              </h4>

              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-stone-400 text-xs block">Original filename</span>
                  <span className="text-stone-700 break-all">{lightbox.original_filename}</span>
                </div>
                {lightbox.display_filename && lightbox.display_filename !== lightbox.original_filename && (
                  <div>
                    <span className="text-stone-400 text-xs block">Display filename</span>
                    <span className="text-stone-700 break-all">{lightbox.display_filename}</span>
                  </div>
                )}
                <div>
                  <span className="text-stone-400 text-xs block">File size</span>
                  <span className="text-stone-700">{formatSize(lightbox.file_size)}</span>
                </div>
                {lightbox.uploaded_by_name && (
                  <div>
                    <span className="text-stone-400 text-xs block">Uploaded by</span>
                    <span className="text-stone-700">{lightbox.uploaded_by_name}</span>
                  </div>
                )}
                {lightbox.created_at && (
                  <div>
                    <span className="text-stone-400 text-xs block">Date</span>
                    <span className="text-stone-700">{formatDate(lightbox.created_at)}</span>
                  </div>
                )}
                {lightbox.face_detection_status && (
                  <div>
                    <span className="text-stone-400 text-xs block">Face detection</span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <FaceStatusDot status={lightbox.face_detection_status} />
                      <span className="text-stone-700 capitalize">{lightbox.face_detection_status}</span>
                      {lightbox.detected_faces_count > 0 && (
                        <span className="text-stone-500">
                          ({lightbox.detected_faces_count} detected, {lightbox.matched_faces_count || 0} matched)
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* DO NOT USE toggle */}
              <div className="pt-2 border-t border-stone-200">
                <button
                  onClick={() => toggleDoNotUse(lightbox)}
                  className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition ${
                    lightbox.do_not_use_marketing
                      ? 'bg-red-50 text-red-700 hover:bg-red-100'
                      : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
                  }`}
                >
                  <Ban className="w-4 h-4" />
                  {lightbox.do_not_use_marketing ? 'Marked: DO NOT USE' : 'Mark as DO NOT USE'}
                </button>
              </div>

              {/* Delete */}
              <button
                onClick={() => handleDelete(lightbox.photo_id)}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium text-red-600 bg-white border border-red-200 hover:bg-red-50 transition"
              >
                <Trash2 className="w-4 h-4" />
                Delete photo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
